import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { Loader2, Search, Flag, EyeOff, Trash2 } from 'lucide-react';
import { format } from 'date-fns';
import SEO from '@/components/SEO';

type FlaggedPost = {
  id: string;
  content: string;
  user_id: string;
  thrive_circle_id: string | null;
  created_at: string;
  is_archived: boolean | null;
};

type FlaggedComment = {
  id: string;
  content: string;
  user_id: string;
  post_id: string;
  created_at: string;
  is_hidden: boolean | null;
};

export default function CommunityModerationPage() {
  const [posts, setPosts] = useState<FlaggedPost[]>([]);
  const [comments, setComments] = useState<FlaggedComment[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; type: 'post' | 'comment'; id: string | null }>({ open: false, type: 'post', id: null });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  useEffect(() => { fetchFlagged(); }, []);

  const fetchFlagged = async () => {
    setIsLoading(true);
    const postsRes = await (supabase as any).from('community_posts').select('id, content, user_id, thrive_circle_id, created_at, is_archived').eq('is_flagged', true).order('created_at', { ascending: false });
    const commentsRes = await (supabase as any).from('community_comments').select('id, content, user_id, post_id, created_at, is_hidden').eq('is_flagged', true).order('created_at', { ascending: false });
    if (postsRes.error) toast({ title: 'Error loading posts', description: postsRes.error.message, variant: 'destructive' });
    else setPosts((postsRes.data as any[]) || []);
    if (commentsRes.error) toast({ title: 'Error loading comments', description: commentsRes.error.message, variant: 'destructive' });
    else setComments((commentsRes.data as any[]) || []);
    setIsLoading(false);
  };

  const archivePost = async (postId: string) => {
    const { error } = await supabase.from('community_posts').update({ is_archived: true }).eq('id', postId);
    if (error) toast({ title: 'Error', description: error.message, variant: 'destructive' });
    else { toast({ title: 'Post archived' }); fetchFlagged(); }
  };

  const hideComment = async (commentId: string) => {
    const { error } = await (supabase as any).from('community_comments').update({ is_hidden: true }).eq('id', commentId);
    if (error) toast({ title: 'Error', description: error.message, variant: 'destructive' });
    else { toast({ title: 'Comment hidden' }); fetchFlagged(); }
  };

  const unflagPost = async (postId: string) => {
    const { error } = await supabase.from('community_posts').update({ is_flagged: false } as any).eq('id', postId);
    if (error) toast({ title: 'Error', description: error.message, variant: 'destructive' });
    else { toast({ title: 'Post unflagged' }); fetchFlagged(); }
  };

  const unflagComment = async (commentId: string) => {
    const { error } = await (supabase as any).from('community_comments').update({ is_flagged: false }).eq('id', commentId);
    if (error) toast({ title: 'Error', description: error.message, variant: 'destructive' });
    else { toast({ title: 'Comment unflagged' }); fetchFlagged(); }
  };

  const confirmDelete = async () => {
    if (!deleteDialog.id) return;
    setIsSubmitting(true);
    const table = deleteDialog.type === 'post' ? 'community_posts' : 'community_comments';
    const { error } = await (supabase as any).from(table).delete().eq('id', deleteDialog.id);
    if (error) toast({ title: 'Error', description: error.message, variant: 'destructive' });
    else { toast({ title: `${deleteDialog.type === 'post' ? 'Post' : 'Comment'} deleted` }); fetchFlagged(); }
    setDeleteDialog({ open: false, type: 'post', id: null });
    setIsSubmitting(false);
  };

  const filteredPosts = posts.filter(p => p.content.toLowerCase().includes(search.toLowerCase()));
  const filteredComments = comments.filter(c => c.content.toLowerCase().includes(search.toLowerCase()));

  return (
    <>
      <SEO title="Moderation" noindex />
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Content Moderation</h1>
          <p className="text-muted-foreground mt-1">Review flagged community posts and comments</p>
        </div>

        <div className="relative max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search flagged content..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" />
        </div>

        {isLoading ? (
          <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div>
        ) : (
          <Tabs defaultValue="posts">
            <TabsList>
              <TabsTrigger value="posts">Flagged Posts ({posts.length})</TabsTrigger>
              <TabsTrigger value="comments">Flagged Comments ({comments.length})</TabsTrigger>
            </TabsList>

            <TabsContent value="posts">
              <Card>
                <CardContent className="pt-6">
                  {filteredPosts.length === 0 ? (
                    <div className="text-center py-12">
                      <Flag className="h-12 w-12 text-muted-foreground/30 mx-auto mb-3" />
                      <p className="text-muted-foreground">No flagged posts.</p>
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Content</TableHead>
                          <TableHead>Author</TableHead>
                          <TableHead>Date</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredPosts.map(post => (
                          <TableRow key={post.id}>
                            <TableCell className="max-w-[300px] truncate">{post.content}</TableCell>
                            <TableCell className="font-mono text-xs">{post.user_id.slice(0, 12)}…</TableCell>
                            <TableCell className="text-sm text-muted-foreground">{format(new Date(post.created_at), 'MMM d, yyyy')}</TableCell>
                            <TableCell>{post.is_archived ? <Badge variant="outline">Archived</Badge> : <Badge variant="destructive">Flagged</Badge>}</TableCell>
                            <TableCell className="text-right">
                              <div className="flex gap-1 justify-end">
                                <Button size="sm" variant="ghost" title="Unflag" onClick={() => unflagPost(post.id)}>Unflag</Button>
                                {!post.is_archived && (
                                  <Button size="sm" variant="ghost" title="Archive" onClick={() => archivePost(post.id)}>
                                    <EyeOff className="h-4 w-4" />
                                  </Button>
                                )}
                                <Button size="sm" variant="ghost" className="text-destructive" onClick={() => setDeleteDialog({ open: true, type: 'post', id: post.id })}>
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="comments">
              <Card>
                <CardContent className="pt-6">
                  {filteredComments.length === 0 ? (
                    <div className="text-center py-12">
                      <Flag className="h-12 w-12 text-muted-foreground/30 mx-auto mb-3" />
                      <p className="text-muted-foreground">No flagged comments.</p>
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Content</TableHead>
                          <TableHead>Author</TableHead>
                          <TableHead>Date</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredComments.map(comment => (
                          <TableRow key={comment.id}>
                            <TableCell className="max-w-[300px] truncate">{comment.content}</TableCell>
                            <TableCell className="font-mono text-xs">{comment.user_id.slice(0, 12)}…</TableCell>
                            <TableCell className="text-sm text-muted-foreground">{format(new Date(comment.created_at), 'MMM d, yyyy')}</TableCell>
                            <TableCell>{comment.is_hidden ? <Badge variant="outline">Hidden</Badge> : <Badge variant="destructive">Flagged</Badge>}</TableCell>
                            <TableCell className="text-right">
                              <div className="flex gap-1 justify-end">
                                <Button size="sm" variant="ghost" title="Unflag" onClick={() => unflagComment(comment.id)}>Unflag</Button>
                                <Button size="sm" variant="ghost" title="Hide" onClick={() => hideComment(comment.id)}>
                                  <EyeOff className="h-4 w-4" />
                                </Button>
                                <Button size="sm" variant="ghost" className="text-destructive" onClick={() => setDeleteDialog({ open: true, type: 'comment', id: comment.id })}>
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        )}
      </div>

      <Dialog open={deleteDialog.open} onOpenChange={open => !open && setDeleteDialog({ open: false, type: 'post', id: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete {deleteDialog.type === 'post' ? 'Post' : 'Comment'}</DialogTitle>
            <DialogDescription>This will permanently delete this {deleteDialog.type}. This action cannot be undone.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog({ open: false, type: 'post', id: null })}>Cancel</Button>
            <Button variant="destructive" onClick={confirmDelete} disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
